import { Controller, FieldError, useFormContext } from 'react-hook-form';
import { forwardRef } from 'react';
import { get, isArray } from 'lodash';
import classNames from 'classnames';
import { FormControl } from './FormControl';
import {
  SearchableDropdown,
  SearchableDropdownProps,
} from '../SearchableDropdown';

export interface FormSearchableDropdownProps extends SearchableDropdownProps {
  name: string;
  className?: string;
  label?: string;
  defaultValue?: any;
  loadingOptions?: boolean;
}

export const FormSearchableDropdown = forwardRef(
  (
    {
      name,
      label,
      className,
      defaultValue,
      ...searchableDropdownProps
    }: FormSearchableDropdownProps,
    ref,
  ) => {
    const {
      control,
      formState: { errors },
    } = useFormContext();
    const error = get(errors, name);

    return (
      <FormControl
        label={label}
        name={name}
        className={classNames(className, 'h-full')}
        error={error as FieldError}
      >
        <Controller
          control={control}
          defaultValue={defaultValue}
          name={name}
          render={({ field }) => {
            const defVal = field.value ?? defaultValue;
            const startingValues = searchableDropdownProps.items.filter(
              (item) =>
                isArray(defVal)
                  ? defVal?.some((val) => val === item.value)
                  : defVal === item.value,
            );

            return (
              <SearchableDropdown
                ref={ref}
                {...searchableDropdownProps}
                value={
                  isArray(defVal) || searchableDropdownProps.multiple
                    ? startingValues
                    : startingValues[0]
                }
                onChange={(selected) => {
                  searchableDropdownProps.onChange?.(selected);
                  field.onChange(
                    selected instanceof Array
                      ? selected.map((item) => item.value)
                      : selected.value,
                  );
                }}
                inputClassName={error ? 'border-red-600' : 'border-gray-300'}
                resultsLoading={searchableDropdownProps.resultsLoading}
                data-test-id={
                  searchableDropdownProps['data-test-id'] ||
                  'searchable-dropdown'
                }
              />
            );
          }}
        />
      </FormControl>
    );
  },
);
